import _ from 'underscore';
import React, { useContext, useMemo, useLayoutEffect } from 'react';
import PropTypes from 'prop-types';
import { PerfMarkerFactory } from '@bingads-webui/performance-marker';

import { PerformanceMarkerContext } from './perf-marker-context';
import type {
  PerformanceMarker,
  PerformanceMarkerChildProviderProps,
  PerformanceMarkerProviderProps,
} from './types';

export const PerformanceMarkerProvider = (props: PerformanceMarkerProviderProps) => {
  const perfMarker =
    useMemo(
      () => {
        PerfMarkerFactory.init(props.instrumentation);
        const appPerfMarker = PerfMarkerFactory.createMarker(props.name) as PerformanceMarker;
        appPerfMarker.willInit();
        appPerfMarker.doneCallback = props.doneCallback;
        return appPerfMarker;
      },
      [props.doneCallback, props.instrumentation, props.name]
    );

  useLayoutEffect(() => {
    if (props.immediateDone && perfMarker) {
      perfMarker.done();
    }
  }, [perfMarker, props.immediateDone]);

  perfMarker.willRender();

  return (
    <PerformanceMarkerContext.Provider value={perfMarker}>
      {props.children}
    </PerformanceMarkerContext.Provider>
  );
};

PerformanceMarkerProvider.propTypes = {
  children: PropTypes.node.isRequired,
  name: PropTypes.string.isRequired,
  instrumentation: PropTypes.shape({}).isRequired,
  immediateDone: PropTypes.bool,
  doneCallback: PropTypes.func,
};

PerformanceMarkerProvider.defaultProps = {
  immediateDone: true,
  doneCallback: _.noop,
};

export const PerformanceMarkerChildProvider = (props: PerformanceMarkerChildProviderProps) => {
  const perfMarkerParent = useContext(PerformanceMarkerContext) as PerformanceMarker;
  const perfMarkerChild = useMemo(
    () => {
      const child = perfMarkerParent.createChild(props.name, { autoEnd: true });
      child.willInit();
      return child;
    },
    [perfMarkerParent, props.name]
  );

  perfMarkerChild.willRender();

  return (
    <PerformanceMarkerContext.Provider value={perfMarkerChild}>
      {props.children}
    </PerformanceMarkerContext.Provider>
  );
};

PerformanceMarkerChildProvider.propTypes = {
  children: PropTypes.node.isRequired,
  name: PropTypes.string.isRequired,
};
